import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Clock, Search, AlertCircle, MessageSquare, CheckCircle } from "lucide-react";
import { ReviewStatus, getStatusLabel, getStatusVariant } from "@/pages/conciliation/stores/conciliation/email-review/types/shared";

export type AggregatedStatus =
  | "PENDING"
  | "IN_ANALYSIS"
  | "AWAITING_STORE"
  | "RESPONDED"
  | "FINALIZED";

export interface ItemsSummary {
  total: number;
  pending_answer: number;
  responded: number;
  rejected_waiting: number;
  approved: number;
}

interface EmailStatusBadgeProps {
  status: AggregatedStatus;
  itemsSummary: ItemsSummary;
}

const STATUS_CONFIG: Record<
  AggregatedStatus,
  {
    label: string;
    className: string;
    Icon: typeof Clock;
  }
> = {
  PENDING: {
    label: "Pendente",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100",
    Icon: Clock,
  },
  IN_ANALYSIS: {
    label: "Em Analise",
    className: "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100",
    Icon: Search,
  },
  AWAITING_STORE: {
    label: "Aguardando Loja",
    className: "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100",
    Icon: AlertCircle,
  },
  RESPONDED: {
    label: "Respondido",
    className: "bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-100",
    Icon: MessageSquare,
  },
  FINALIZED: {
    label: "Finalizado",
    className: "bg-green-100 text-green-800 border-green-200 hover:bg-green-100",
    Icon: CheckCircle,
  },
};

export function EmailStatusBadge({ status, itemsSummary }: EmailStatusBadgeProps) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  const Icon = config.Icon;

  const tooltipParts: string[] = [];

  if (itemsSummary.pending_answer > 0) {
    tooltipParts.push(`${itemsSummary.pending_answer} pendente(s)`);
  }
  if (itemsSummary.responded > 0) {
    tooltipParts.push(`${itemsSummary.responded} respondido(s)`);
  }
  if (itemsSummary.rejected_waiting > 0) {
    tooltipParts.push(`${itemsSummary.rejected_waiting} aguardando`);
  }
  if (itemsSummary.approved > 0) {
    tooltipParts.push(`${itemsSummary.approved} aprovado(s)`);
  }

  const tooltipContent = tooltipParts.length > 0 ? tooltipParts.join(", ") : "Sem itens";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`${config.className} flex items-center gap-1 cursor-default`}>
            <Icon className="w-3 h-3" />
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipContent}</p>
          <p className="text-xs text-gray-500">Total: {itemsSummary.total} itens</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Re-exporta ItemStatus para manter compatibilidade com imports existentes
export type ItemStatus = ReviewStatus;

interface ItemStatusBadgeProps {
  status: string | null;
}

export function ItemStatusBadge({ status }: ItemStatusBadgeProps) {
  if (!status) {
    return (
      <Badge variant="secondary" className="text-xs">
        Desconhecido
      </Badge>
    );
  }

  return (
    <Badge variant={getStatusVariant(status)} className="text-xs">
      {getStatusLabel(status)}
    </Badge>
  );
}
